import { useState } from 'react';

import { useMiniAppSDK } from '@context/SDKConfigContext.tsx';
import { useSDKHandler } from '@features/method-tester/hooks/index.ts';

import { VERSION_0_3 } from '@/constant';
import { useJsBridgeVersion } from '@/context/JsBridgeVersionContext.tsx';
import { ERROR_CODE_MESSAGE_MAP } from '@/features/method-tester/constants';
import { OPERATION_KEY } from '@/features/method-tester/types/SDKCallResult.ts';
import { Button } from '@asphalt-react/button';
import { Card } from '@asphalt-react/card';
import { Loader } from '@asphalt-react/loader';
import { Textfield } from '@asphalt-react/textfield';
import { Heading } from '@asphalt-react/typography';
import { GetConsentResult } from '@gopay/miniapp-client';

export const Consent = () => {
  const {
    isLoading,
    response: consent,
    invokeSDKActionV0_3,
    invokeSDKActionV1,
  } = useSDKHandler<GetConsentResult>();
  const sdk = useMiniAppSDK();
  const { version } = useJsBridgeVersion();
  const [selectedErrorCode, setSelectedErrorCode] = useState<number | null>(
    null
  );

  const [consentName, setConsentName] = useState<string>('personalDetails');

  const getConsent = async () => {
    try {
      const options = {
        ...(selectedErrorCode && {
          error: {
            operation_key: OPERATION_KEY.CONSENT,
            code: selectedErrorCode,
          },
        }),
      };
      if (version === VERSION_0_3) {
        await invokeSDKActionV0_3(() => sdk.getConsent(consentName), options);
      } else {
        await invokeSDKActionV1('GPConsent', 'getUserConsent', {
          consent_name: consentName,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const resetSelection = () => setSelectedErrorCode(null);

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConsentName(event.target.value);
  };

  return (
    <Card outlined={true}>
      <div className="flex w-full justify-between">
        <Heading h6>User Consent</Heading>

        <Button size="xs" onClick={getConsent} disabled={isLoading}>
          Get User Consent
        </Button>
      </div>
      <div className="flex gap-4">
        {ERROR_CODE_MESSAGE_MAP.consent.map(({ code, message }) => (
          <label key={code} className="flex items-center gap-1">
            <input
              type="radio"
              name="ERROR_CODE"
              value={code}
              checked={selectedErrorCode === code}
              onChange={() => setSelectedErrorCode(code)}
            />
            {message}
          </label>
        ))}
      </div>
      <Button size="xs" onClick={resetSelection}>
        Reset Error Code
      </Button>
      <div className="pt-2">
        <Textfield
          type="text"
          placeholder="Enter the consent name"
          value={consentName}
          onChange={onInputChange}
          stretch={true}
          size="s"
        ></Textfield>
      </div>
      {isLoading && <Loader />}
      {!isLoading && consent && (
        <div className="mt-2 w-full">
          <pre
            data-testid="consent-response"
            className="overflow-auto text-sm bg-gray-100 p-2 rounded"
          >
            {JSON.stringify(consent, null, 2)}
          </pre>
        </div>
      )}
    </Card>
  );
};
